//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { Placement } from 'tippy.js';

import ActionButton from '@components/atoms/action-button/ActionButton';
import Anchor, { ButtonStyleAnchor } from '@components/atoms/anchor/Anchor';
import Button, { IButtonProps } from '@components/atoms/button/Button';
import {
  DangerButtonStyles,
  DefaultButtonStyles,
  IButtonStateStyles,
  InvisibleButtonStyles,
  OverlayIconButtonStyles,
  PrimaryButtonStyles,
  PrimaryLinkButtonStyles,
  SecondaryButtonStyles,
} from '@components/atoms/button/buttonStyles';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import {
  hasPermissionForAllTasks,
  hasPermissionForAnyTask,
} from '@services/permissions';
import { ITrackableComponent } from '@utils/withTracking';

interface CommonRequiredPermissionsProps {
  className?: string;
  /**
   * Disables the button regardless of whether the current user has the given permission.
   */
  isDisabled?: boolean;
  isTooltipDisabled?: boolean;
  /**
   * If set to true, we will show a tooltip when hovering over the disabled button, regardless
   * of whether the caller has the appropriate permissions.
   */
  alwaysShowTooltip?: boolean;
  permissionNames: string[];
  placement?: Placement;
  tooltipContents?: React.ReactChild;
  shouldRequireAllPermissions?: boolean;
}

interface Props
  extends React.HTMLAttributes<HTMLButtonElement>,
    Partial<React.ComponentProps<typeof ActionButton>>,
    Partial<ITrackableComponent>,
    Partial<IButtonProps>,
    CommonRequiredPermissionsProps {
  isActionButton?: boolean;
}

export const MultiRequiredPermissionsButton = forwardRef<
  HTMLButtonElement,
  Props
>(
  (
    {
      alwaysShowTooltip = false,
      buttonStyles = PrimaryButtonStyles,
      children,
      className = '',
      iconColor,
      iconName,
      interactive,
      isActionButton,
      isDisabled = false,
      isTooltipDisabled,
      label,
      onClick,
      permissionNames,
      placement,
      shouldRequireAllPermissions = true,
      tooltipContents = DEFAULT_TOOLTIP_CONTENT,
      trackingId,
      ...buttonProps
    },
    ref,
  ) => {
    const hasPermission = shouldRequireAllPermissions
      ? hasPermissionForAllTasks(permissionNames)
      : hasPermissionForAnyTask(permissionNames);

    const shouldDisableButton = isDisabled || !hasPermission;

    if (isActionButton && iconName) {
      return (
        <ActionButton
          className={className}
          disabledPopoverContent={tooltipContents}
          iconColor={iconColor}
          iconName={iconName}
          interactive={interactive}
          isDisabled={shouldDisableButton}
          label={label}
          onClick={onClick}
          placement={placement}
          ref={ref}
          trackingId={trackingId}
          {...buttonProps}
        />
      );
    } else {
      return (
        <SmallTooltip
          appendTarget='body'
          content={tooltipContents}
          interactive={interactive}
          isDisabled={
            !alwaysShowTooltip && (hasPermission || isTooltipDisabled)
          }
          placement={placement}
          target={
            <Button
              buttonStyles={buttonStyles}
              className={className}
              iconColor={iconColor}
              iconName={iconName}
              isDisabled={shouldDisableButton}
              onClick={onClick}
              ref={ref}
              trackingId={trackingId}
              {...buttonProps}
            >
              {children}
            </Button>
          }
        />
      );
    }
  },
);
MultiRequiredPermissionsButton.displayName = 'MultiRequiredPermissionsButton';

interface MultiRequiredPermissionsAnchorProps
  extends React.ComponentProps<typeof Anchor>,
    CommonRequiredPermissionsProps {
  buttonStyles?: IButtonStateStyles;
  isInteractive?: React.ComponentProps<typeof SmallTooltip>['interactive'];
}

export const MultiRequiredPermissionsAnchor = ({
  alwaysShowTooltip = false,
  buttonStyles = PrimaryButtonStyles,
  isDisabled = false,
  isInteractive,
  isTooltipDisabled,
  permissionNames,
  placement,
  shouldRequireAllPermissions = true,
  tooltipContents = DEFAULT_TOOLTIP_CONTENT,
  trackingId,
  ...anchorProps
}: MultiRequiredPermissionsAnchorProps) => {
  const hasPermission = shouldRequireAllPermissions
    ? hasPermissionForAllTasks(permissionNames)
    : hasPermissionForAnyTask(permissionNames);

  const shouldDisableButton = isDisabled || !hasPermission;

  return (
    <SmallTooltip
      appendTarget='body'
      content={tooltipContents}
      interactive={isInteractive}
      isDisabled={!alwaysShowTooltip && (hasPermission || isTooltipDisabled)}
      placement={placement}
      target={
        <ButtonStyleAnchor
          buttonStyles={buttonStyles}
          isDisabled={shouldDisableButton}
          trackingId={trackingId}
          {...anchorProps}
        />
      }
    />
  );
};

interface RequiredPermissionsAnchorProps
  extends Omit<MultiRequiredPermissionsAnchorProps, 'permissionNames'> {
  permissionName: string;
}

export const RequiredPermissionsAnchor = ({
  permissionName,
  ...props
}: RequiredPermissionsAnchorProps) => {
  return (
    <MultiRequiredPermissionsAnchor
      permissionNames={[permissionName]}
      {...props}
    />
  );
};

type MultiRequiredPermissionsActionButtonProps = Omit<
  Props,
  'isActionButton' | 'ref'
>;

export const MultiRequiredPermissionsActionButton = forwardRef<
  HTMLButtonElement,
  MultiRequiredPermissionsActionButtonProps
>(({ permissionNames, ...props }, ref) => {
  return (
    <MultiRequiredPermissionsButton
      {...props}
      isActionButton={true}
      permissionNames={permissionNames}
      ref={ref}
    />
  );
});
MultiRequiredPermissionsActionButton.displayName =
  'MultiRequiredPermissionsActionButton';

interface RequiredPermissionsButtonProps
  extends Omit<Props, 'permissionNames' | 'ref'> {
  permissionName: string;
}

export const RequiredPermissionsButton = forwardRef<
  HTMLButtonElement,
  RequiredPermissionsButtonProps
>(({ permissionName, ...props }, ref) => {
  return (
    <MultiRequiredPermissionsButton
      {...props}
      permissionNames={[permissionName]}
      ref={ref}
    />
  );
});
RequiredPermissionsButton.displayName = 'RequiredPermissionsButton';

interface RequiredPermissionsActionButtonProps
  extends Omit<Props, 'permissionNames' | 'isActionButton' | 'ref'> {
  permissionName: string;
}

export const RequiredPermissionsActionButton = forwardRef<
  HTMLButtonElement,
  RequiredPermissionsActionButtonProps
>(({ permissionName, ...props }, ref) => {
  return (
    <MultiRequiredPermissionsButton
      {...props}
      isActionButton={true}
      permissionNames={[permissionName]}
      ref={ref}
    />
  );
});
RequiredPermissionsActionButton.displayName = 'RequiredPermissionsActionButton';

type PrestyledProps = Omit<
  RequiredPermissionsButtonProps,
  'buttonStyles' | 'permissionNames'
>;

const StyledTooltipText = styled.div`
  white-space: normal;
`;

const DEFAULT_TOOLTIP_CONTENT = (
  <StyledTooltipText>
    You don't have permission to complete this action. Contact a group admin if
    this is a mistake.
  </StyledTooltipText>
);

export const MultiRequiredPermissionsSecondaryButton = forwardRef<
  HTMLButtonElement,
  Props
>(({ ...props }, ref) => {
  return (
    <MultiRequiredPermissionsButton
      {...props}
      buttonStyles={SecondaryButtonStyles}
      ref={ref}
    />
  );
});

MultiRequiredPermissionsSecondaryButton.displayName =
  'MultiRequiredPermissionsSecondaryButton';

export const RequiredPermissionsSecondaryButton = (props: PrestyledProps) => (
  <RequiredPermissionsButton buttonStyles={SecondaryButtonStyles} {...props} />
);

export const RequiredPermissionsDangerButton = (props: PrestyledProps) => (
  <RequiredPermissionsButton buttonStyles={DangerButtonStyles} {...props} />
);

export const RequiredPermissionsDefaultButton = (props: PrestyledProps) => (
  <RequiredPermissionsButton buttonStyles={DefaultButtonStyles} {...props} />
);

export const RequiredPermissionsOverlayIconButton = (props: PrestyledProps) => (
  <RequiredPermissionsButton
    buttonStyles={OverlayIconButtonStyles}
    {...props}
  />
);

export const MultiRequiredPermissionsLinkButton = forwardRef<
  HTMLButtonElement,
  Props
>(({ ...props }, ref) => {
  return (
    <MultiRequiredPermissionsButton
      {...props}
      buttonStyles={PrimaryLinkButtonStyles}
      ref={ref}
    />
  );
});

MultiRequiredPermissionsLinkButton.displayName =
  'MultiRequiredPermissionsLinkButton';

export const RequiredPermissionsLinkButton = forwardRef<
  HTMLButtonElement,
  RequiredPermissionsButtonProps
>(({ ...props }, ref) => {
  return (
    <RequiredPermissionsButton
      {...props}
      buttonStyles={PrimaryLinkButtonStyles}
      ref={ref}
    />
  );
});

RequiredPermissionsLinkButton.displayName = 'RequiredPermissionsLinkButton';

export const RequiredPermissionsInvisibleButton = (props: PrestyledProps) => (
  <RequiredPermissionsButton buttonStyles={InvisibleButtonStyles} {...props} />
);

export default RequiredPermissionsButton;
